import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Tabs,
	Tab,
	CircularProgress,
	TablePagination,
	IconButton,
	Tooltip,
	Typography,
	Snackbar,
	Alert,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import {
	createEvent,
	getAllEvent,
	GetAllMenuTypes,
	updateEvent,
	updatemenutype,
	createMenutype,
	deleteMenuType,
	deleteEventType,
} from '../../../services/api/Event';
import MenuEventForm from './MenuEventForm'; // Import the new form component

interface MenuType {
	_id: string;
	menuType: string;
}

interface EventType {
	_id: string;
	eventType: string;
	menuType: string[];
}

const MenuEventManagement: React.FC = () => {
	const [activeTab, setActiveTab] = useState<number>(0);
	const [menuTypes, setMenuTypes] = useState<MenuType[]>([]);
	const [eventTypes, setEventTypes] = useState<EventType[]>([]);
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [currentItem, setCurrentItem] = useState<MenuType | EventType | null>(null);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
	const [snackbarMessage, setSnackbarMessage] = useState<string>('');
	const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
	const [loading, setLoading] = useState<boolean>(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [itemToDelete, setItemToDelete] = useState<MenuType | EventType | null>(null);
	const [isMenuTypeDelete, setIsMenuTypeDelete] = useState<boolean>(false);

	// Pagination state
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(4);
	const [totalMenuTypes, setTotalMenuTypes] = useState(0);
	const [totalEventTypes, setTotalEventTypes] = useState(0);

	// Memoized fetch functions
	const fetchMenuTypes = useCallback(async () => {
		setLoading(true);
		try {
			const response = await GetAllMenuTypes();
			const data = response?.data?.menuTypes;
			setMenuTypes(data || []);
			setTotalMenuTypes(data?.length || 0);
		} catch (error) {
			showSnackbar('Failed to fetch menu types', 'error');
		} finally {
			setLoading(false);
		}
	}, []);

	const fetchEventTypes = useCallback(async () => {
		setLoading(true);
		try {
			const response = await getAllEvent();
			const data = response.data.eventTypes;
			setEventTypes(data || []);
			setTotalEventTypes(data?.length || 0);
		} catch (error) {
			showSnackbar('Failed to fetch event types', 'error');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchMenuTypes();
		fetchEventTypes();
	}, [fetchMenuTypes, fetchEventTypes]);

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
		setPage(0); // Reset to first page when switching tabs
	};

	const handleOpenAddDialog = () => {
		setCurrentItem(null);
		setIsEditing(false);
		setOpenDialog(true);
	};

	const handleOpenEditDialog = (item: MenuType | EventType) => {
		setCurrentItem(item);
		setIsEditing(true);
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleMenuTypeSubmit = async (data: { menuType: string }) => {
		try {
			const response =
				isEditing && currentItem?._id
					? await updatemenutype(data, currentItem._id)
					: await createMenutype(data);

			if (response.success) {
				showSnackbar(response.data.message, 'success');
				await fetchMenuTypes();
				handleCloseDialog();
			} else {
				showSnackbar(response.data.errors || 'Operation failed', 'error');
			}
		} catch (error) {
			showSnackbar('An error occurred', 'error');
		}
	};

	const handleEventTypeSubmit = async (data: { eventType: string; menuType: string[] }) => {
		try {
			const response =
				isEditing && currentItem?._id
					? await updateEvent(data, currentItem._id)
					: await createEvent(data);

			if (response.success) {
				showSnackbar(response.data.message, 'success');
				await fetchEventTypes();
				handleCloseDialog();
			} else {
				showSnackbar(response.data.message || 'Operation failed', 'error');
			}
		} catch (error) {
			showSnackbar('An error occurred', 'error');
		}
	};

	const handleOpenDeleteDialog = (item: MenuType | EventType, isMenuType: boolean) => {
		setItemToDelete(item);
		setIsMenuTypeDelete(isMenuType);
		setDeleteDialogOpen(true);
	};

	const handleCloseDeleteDialog = () => {
		setDeleteDialogOpen(false);
		setItemToDelete(null);
	};

	const handleConfirmDelete = async () => {
		if (itemToDelete) {
			try {
				const response = isMenuTypeDelete
					? await deleteMenuType(itemToDelete._id)
					: await deleteEventType(itemToDelete._id);

				if (response.success) {
					showSnackbar(response?.data?.message || 'Deleted successfully', 'success');
					isMenuTypeDelete ? await fetchMenuTypes() : await fetchEventTypes();
				} else {
					showSnackbar(response.data.message || 'Delete failed', 'error');
				}
			} catch (error) {
				showSnackbar('An error occurred during deletion', 'error');
			} finally {
				handleCloseDeleteDialog();
			}
		}
	};

	const showSnackbar = (message: string, severity: 'success' | 'error') => {
		setSnackbarMessage(message);
		setSnackbarSeverity(severity);
		setSnackbarOpen(true);
	};

	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
	};

	// Pagination handlers
	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Calculate the current items to display based on pagination
	const getCurrentMenuTypes = () =>
		menuTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const getCurrentEventTypes = () =>
		eventTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	return (
		<Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				sx={{ mb: 3 }}
				indicatorColor="primary"
				textColor="primary"
			>
				<Tab label="Menu Types" />
				<Tab label="Event Types" />
			</Tabs>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
				<Button
					variant="contained"
					startIcon={<Add />}
					onClick={handleOpenAddDialog}
					color="primary"
				>
					Add {activeTab === 0 ? 'Menu Type' : 'Event Type'}
				</Button>
			</Box>

			{loading ? (
				<Box display="flex" justifyContent="center" mt={4}>
					<CircularProgress />
				</Box>
			) : activeTab === 0 ? (
				<>
					<TableContainer component={Paper} elevation={3}>
						<Table>
							<TableHead sx={{ bgcolor: 'background.paper' }}>
								<TableRow>
									<TableCell sx={{ fontWeight: 600 }}>Menu Type</TableCell>
									<TableCell align="right" sx={{ fontWeight: 600 }}>
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
								{menuTypes.length > 0 ? (
									getCurrentMenuTypes().map((menuType) => (
										<TableRow key={menuType._id} hover>
											<TableCell>{menuType.menuType}</TableCell>
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
												<Typography
													sx={{ display: 'flex', justifyContent: 'end' }}
												>
													<Tooltip title="Edit">
														<IconButton
															onClick={() =>
																handleOpenEditDialog(menuType)
															}
														>
															<Edit />
														</IconButton>
													</Tooltip>
													<Tooltip title="Delete">
														<IconButton
															onClick={() =>
																handleOpenDeleteDialog(
																	menuType,
																	true
																)
															}
														>
															<Delete />
														</IconButton>
													</Tooltip>
												</Typography>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={2} align="center">
											No menu types found
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={[5, 10, 25]}
						component="div"
						count={totalMenuTypes}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</>
			) : (
				<>
					<TableContainer component={Paper} elevation={3}>
						<Table>
							<TableHead sx={{ bgcolor: 'background.paper' }}>
								<TableRow>
									<TableCell sx={{ fontWeight: 600 }}>Event Type</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Menu Types</TableCell>
									<TableCell align="right" sx={{ fontWeight: 600 }}>
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{eventTypes.length > 0 ? (
									getCurrentEventTypes().map((eventType) => (
										<TableRow key={eventType._id} hover>
											<TableCell>{eventType.eventType}</TableCell>
											<TableCell>
												<Box
													sx={{
														display: 'flex',
														flexWrap: 'wrap',
														gap: 0.5,
													}}
												>
													{eventType.menuType.map((menuName, index) => (
														<Chip
															key={`${menuName}-${index}`}
															label={menuName}
															size="small"
															color="primary"
															variant="outlined"
														/>
													))}
												</Box>
											</TableCell>
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
												<Typography
													sx={{ display: 'flex', justifyContent: 'end' }}
												>
													<Tooltip title="Edit">
														<IconButton
															onClick={() =>
																handleOpenEditDialog(eventType)
															}
														>
															<Edit />
														</IconButton>
													</Tooltip>
													<Tooltip title="Delete">
														<IconButton
															onClick={() =>
																handleOpenDeleteDialog(
																	eventType,
																	false
																)
															}
														>
															<Delete />
														</IconButton>
													</Tooltip>
												</Typography>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={3} align="center">
											No event types found
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						rowsPerPageOptions={[5, 10, 25]}
						component="div"
						count={totalEventTypes}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</>
			)}

			<MenuEventForm
				open={openDialog}
				onClose={handleCloseDialog}
				onSubmit={activeTab === 0 ? handleMenuTypeSubmit : handleEventTypeSubmit}
				isEditing={isEditing}
				activeTab={activeTab}
				menuTypes={menuTypes}
				currentItem={currentItem}
				setActiveTab={setActiveTab}
			/>

			<Dialog
				open={deleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>
					<Typography variant="body1">
						Are you sure you want to delete this{' '}
						{isMenuTypeDelete ? 'menu type' : 'event type'}? This action cannot be
						undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDeleteDialog} color="primary">
						Cancel
					</Button>
					<Button onClick={handleConfirmDelete} color="error" variant="contained">
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbarSeverity}
					sx={{ width: '100%' }}
					elevation={6}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default MenuEventManagement;
